import type {
  LinkTarget,
  PublicWidgetApp,
  PublicWidgetSettings,
  WidgetAppParams,
  WidgetBehaviorType,
} from '@worknet/models';

export function withQuery(urlStr: string, params: Record<string, string | undefined | null>) {
  try {
    const url = new URL(urlStr, window.location.href);
    Object.entries(params).forEach(([k, v]) => {
      if (v == null || v === '') {
        return;
      }
      if (!url.searchParams.has(k)) {
        url.searchParams.set(k, v);
      }
    });
    return url.toString();
  } catch {
    return urlStr;
  }
}

function isJwtAuthVariation(variation: PublicWidgetSettings): boolean {
  const authRequired = variation.authRequired;
  return (
    typeof authRequired === 'object' &&
    authRequired !== null &&
    'type' in authRequired &&
    (authRequired as { type: string }).type === 'jwt'
  );
}

export function getWidgetAppParams(widget: PublicWidgetApp): WidgetAppParams {
  /**
    SSOName is considered as required if it was specified for the All Authenticated Users variation.
    We are picking the first variation in a list that has authRequired and requiredSSOName and no roles specified.
    List order matters. Role based variations with requiredSSOName are not supported for the moment.
   */
  const allAuthUserVariation = widget.settingsVariations.find(
    (v) => v.authRequired && !!v.requiredSSOName && !v.roleIds?.length
  );
  const isSingle = widget.settingsVariations.length === 1;
  const hasAnon = Boolean(widget.settingsVariations.find((v) => !v.authRequired));
  const hasJwt = widget.settingsVariations.some(isJwtAuthVariation);

  const behaviorType: WidgetBehaviorType = isSingle
    ? hasAnon
      ? 'onlyAnon'
      : 'onlyAuth'
    : hasAnon
      ? 'bothAnonAuth'
      : 'onlyAuth';

  return {
    widgetAppName: widget.name,
    requiredSSOName: allAuthUserVariation?.requiredSSOName,
    behaviorType,
    supportsJwt: hasJwt,
  };
}

export function shouldUseJwtAuth(
  widgetAppParams: WidgetAppParams,
  jwtToken?: string | null
): boolean {
  return Boolean(widgetAppParams.supportsJwt && jwtToken);
}

export function resolveIframePath(
  widgetAppParams: WidgetAppParams,
  jwtToken?: string | null
): 'jwt-iframe' | 'anon-iframe' | 'iframe' {
  if (shouldUseJwtAuth(widgetAppParams, jwtToken)) {
    return 'jwt-iframe';
  }

  if (widgetAppParams.behaviorType === 'onlyAnon') {
    return 'anon-iframe';
  }

  return 'iframe';
}

// A local copy of `linkTargets` from `@worknet/models`, not an import of it:
// every other reference to that package in the widget is `import type`, which
// disappears at build time. A value import pulls the package barrel into
// embed.js — it cost 1.2 kB gzipped in unrelated data (a MIME table) before this
// was inlined, and would land whatever the shared package grows into on every
// customer's page. Keying by `LinkTarget` makes the compiler reject the copy if
// the shared union ever gains or loses a value.
const LINK_TARGETS: Record<LinkTarget, true> = {
  _blank: true,
  _self: true,
  _top: true,
  _parent: true,
  auto: true,
};

export function resolveLinkTargetSetting(value: unknown): LinkTarget {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(LINK_TARGETS, value)
    ? (value as LinkTarget)
    : '_blank';
}

// For anchors the widget renders on the embedder's page itself (the nudge box)
// rather than inside the chat iframe, so `auto` only has to tell same-origin
// links apart from external ones.
export function resolveHostPageLinkTarget(setting: LinkTarget, href?: string) {
  const target = setting === 'auto' ? resolveAutoHostPageTarget(href) : setting;

  return {
    target,
    ...(target === '_blank' ? { rel: 'noopener noreferrer' } : {}),
  };
}

function resolveAutoHostPageTarget(href?: string): LinkTarget {
  if (!href) {
    return '_blank';
  }

  try {
    const url = new URL(href, window.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return '_blank';
    }
    return url.origin === window.location.origin ? '_self' : '_blank';
  } catch {
    return '_blank';
  }
}

export function parseBoolLike(value: unknown, defaultValue: boolean): boolean {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  const s = String(value).trim().toLowerCase();
  if (/^(false|0|no|off)$/.test(s)) {
    return false;
  }
  if (/^(true|1|yes|on)$/.test(s)) {
    return true;
  }
  return defaultValue;
}

export function resolveIsDrawer(raw: unknown): boolean {
  return parseBoolLike(raw, true);
}

type Builtin =
  | string
  | number
  | boolean
  | bigint
  | symbol
  | undefined
  | null
  | Date
  | RegExp
  // eslint-disable-next-line @typescript-eslint/ban-types
  | Function
  | Error
  | WeakMap<object, unknown>
  | WeakSet<object>
  | Promise<unknown>;

export type DeepPartial<T> = T extends Builtin
  ? T
  : T extends Map<infer K, infer V>
    ? Map<DeepPartial<K>, DeepPartial<V>>
    : T extends ReadonlyMap<infer K, infer V>
      ? ReadonlyMap<DeepPartial<K>, DeepPartial<V>>
      : T extends Set<infer U>
        ? Set<DeepPartial<U>>
        : T extends ReadonlySet<infer U>
          ? ReadonlySet<DeepPartial<U>>
          : T extends Array<infer U>
            ? Array<DeepPartial<U>>
            : T extends ReadonlyArray<infer U>
              ? ReadonlyArray<DeepPartial<U>>
              : T extends object
                ? { [P in keyof T]?: DeepPartial<T[P]> }
                : T;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PlainObject = Record<string, any>;

export function deepMerge<T extends PlainObject, U extends PlainObject>(
  target: T,
  source: U
): T & U {
  const output: PlainObject = { ...target };

  for (const [key, value] of Object.entries(source)) {
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      typeof output[key] === 'object' &&
      !Array.isArray(output[key])
    ) {
      // recurse into nested objects
      output[key] = deepMerge(output[key], value);
    } else {
      // replace (arrays or primitives)
      output[key] = value;
    }
  }

  return output as T & U;
}

export function sizeToPixelNumber(
  value: string | number,
  dimension: 'width' | 'height' = 'width'
): number {
  if (typeof value === 'number') {
    return value;
  }

  const trimmed = value.toString().trim();
  if (trimmed.includes('%')) {
    const percentage = parseFloat(trimmed);
    if (!Number.isFinite(percentage) || percentage <= 0) {
      return 0;
    }
    const referenceValue = dimension === 'width' ? window.innerWidth : window.innerHeight;
    return (referenceValue * percentage) / 100;
  }

  const match = /^([0-9]+(?:\.[0-9]+)?)/.exec(trimmed);
  const parsedValue = match ? parseFloat(match[1]) : 0;

  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
}
